import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, AlertTriangle, CheckCircle2, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { api, inr } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "../components/ui/sonner";
import Logo from "../components/Logo";

const SERVICES = [
  { key: "standard", label: "STANDARD DELIVERY", tag: "Built in days" },
  { key: "specialist", label: "RUSH HOUR — VIBE CODING SPECIALIST", tag: "Live build session" },
  { key: "super", label: "RUSH HOUR — SUPER VIBE CODER", tag: "Live build session, senior" },
];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const [params] = useSearchParams();
  const { settings } = useSite();
  const { currency, setCurrency, currencies, format } = useCurrency();

  const [config, setConfig] = useState(null);
  const [service, setService] = useState(params.get("service") || "standard");
  const [hours, setHours] = useState(parseFloat(params.get("hours")) || 10);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", company: "", country: "", address: "" });
  const [terms, setTerms] = useState(false);
  const [quote, setQuote] = useState(null);
  const [phase, setPhase] = useState("form"); // form | paying | success | error
  const [result, setResult] = useState(null);
  const quoteTimer = useRef(null);

  useEffect(() => {
    api.get("/checkout/config").then((res) => setConfig(res.data)).catch(() => setConfig({ payments_enabled: false, payable_currencies: ["INR"] }));
  }, []);

  useEffect(() => {
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => {
      api
        .post("/checkout/quote", { service, hours, currency })
        .then((res) => setQuote(res.data))
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(quoteTimer.current);
  }, [service, hours, currency]);

  const set = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });

  const payable = config?.payments_enabled && quote?.payable;
  const totalDisplay = quote ? (currency === "INR" ? inr(quote.total_inr) : quote.converted_total !== null ? format(quote.converted_total) : "—") : "—";

  const pay = async () => {
    setPhase("paying");
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("sdk");
      const res = await api.post("/checkout/create-order", {
        service, hours, currency, terms_accepted: terms, customer,
      });
      const { payment_id, order_id, amount, key_id } = res.data;
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: "Built2Last.live",
        description: `${SERVICES.find((s) => s.key === service)?.label} — ${hours}h`,
        prefill: { name: customer.name, email: customer.email, contact: customer.phone },
        theme: { color: "#F97316" },
        modal: { ondismiss: () => setPhase("form") },
        handler: async (resp) => {
          try {
            await api.post("/checkout/verify", {
              payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            const st = await api.get(`/checkout/status/${payment_id}`);
            setResult(st.data);
            setPhase("success");
            window.scrollTo(0, 0);
          } catch {
            // Payment may still be confirmed via webhook — show status lookup
            setResult({ payment: { payment_id, status: "VERIFYING" }, invoice: null });
            setPhase("success");
          }
        },
      });
      rzp.on("payment.failed", () => setPhase("error"));
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "payments_not_configured") toast.error("Online payments are not activated yet. Please request an estimate instead.");
      else if (detail === "currency_unavailable") toast.error("This payment currency is currently unavailable. Please select another currency.");
      else toast.error("Could not start payment. Please try again.");
      setPhase(detail ? "form" : "error");
    }
  };

  if (phase === "success") {
    const p = result?.payment || {};
    const inv = result?.invoice;
    return (
      <div className="pt-40 pb-32" data-testid="checkout-success">
        <div className="container-x max-w-2xl text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 className="mt-8 font-heading text-4xl sm:text-5xl font-black tracking-tight text-white">
            {p.status === "PAID" ? "PAYMENT CONFIRMED." : "VERIFYING PAYMENT…"}
          </h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            {p.status === "PAID"
              ? "Your development engagement is activated. Your invoice is ready and our team will reach out to kick off."
              : "Your payment is being confirmed by our server. You will receive confirmation shortly."}
          </p>
          {inv && (
            <Link to={`/invoice/${p.payment_id}`} data-testid="checkout-invoice-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold px-8 py-4 transition-colors">
              VIEW INVOICE {inv.invoice_number} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="checkout-page">
      <div className="container-x max-w-5xl">
        <p className="section-label">SECURE CHECKOUT</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white">
          START YOUR <span className="text-flame">BUILD.</span>
        </h1>

        {phase === "error" && (
          <div className="mt-8 rounded-2xl border border-red-500/40 bg-red-500/10 p-6" data-testid="checkout-error">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <p className="font-heading font-bold text-red-300">Payment was not completed.</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={() => setPhase("form")} data-testid="checkout-try-again" className="bg-flame hover:bg-flame-hover text-white">TRY AGAIN</Button>
              <Button variant="outline" onClick={() => { setCurrency("INR"); setPhase("form"); }} data-testid="checkout-change-currency" className="border-white/25 text-slate-200">CHANGE CURRENCY</Button>
              <Button asChild variant="outline" className="border-emerald-500/40 text-emerald-400">
                <a href={`https://wa.me/${settings.whatsapp_number}`} target="_blank" rel="noreferrer" data-testid="checkout-contact-support">
                  <MessageCircle className="w-4 h-4 mr-2" /> CONTACT SUPPORT
                </a>
              </Button>
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 items-start">
          <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-10 space-y-8">
            <div>
              <label className="section-label block mb-4">PROJECT / SERVICE</label>
              <div className="space-y-3">
                {SERVICES.map((s) => (
                  <button key={s.key} type="button" data-testid={`checkout-service-${s.key}`} onClick={() => setService(s.key)}
                    className={`w-full text-left rounded-xl border px-5 py-4 transition-colors ${service === s.key ? "border-flame bg-flame/10" : "border-white/10 hover:border-white/25"}`}>
                    <p className="font-heading font-bold text-sm text-white">{s.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{s.tag}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="section-label block mb-3">DEVELOPMENT HOURS</label>
              <Input type="number" min={1} max={500} value={hours} onChange={(e) => setHours(Math.max(1, parseFloat(e.target.value) || 1))} data-testid="checkout-hours" className="bg-navy-900/60 border-white/10 h-12 w-40" />
            </div>

            <div>
              <label className="section-label block mb-3">CURRENCY</label>
              <div className="flex flex-wrap gap-2" data-testid="checkout-currency-picker">
                {currencies.map((c) => (
                  <button key={c.code} type="button" data-testid={`checkout-currency-${c.code}`} onClick={() => setCurrency(c.code)}
                    className={`rounded-full px-4 py-2 font-mono text-[11px] font-bold tracking-wider transition-colors ${currency === c.code ? "bg-flame text-white" : "border border-white/15 text-slate-300 hover:border-flame/50"}`}>
                    {c.code}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="section-label block mb-4">CUSTOMER DETAILS</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input placeholder="Full name *" value={customer.name} onChange={set("name")} data-testid="checkout-name" className="bg-navy-900/60 border-white/10 h-12" />
                <Input type="email" placeholder="Email *" value={customer.email} onChange={set("email")} data-testid="checkout-email" className="bg-navy-900/60 border-white/10 h-12" />
                <Input placeholder="Phone" value={customer.phone} onChange={set("phone")} data-testid="checkout-phone" className="bg-navy-900/60 border-white/10 h-12" />
                <Input placeholder="Company" value={customer.company} onChange={set("company")} data-testid="checkout-company" className="bg-navy-900/60 border-white/10 h-12" />
                <Input placeholder="Country" value={customer.country} onChange={set("country")} data-testid="checkout-country" className="bg-navy-900/60 border-white/10 h-12" />
                <Input placeholder="Address" value={customer.address} onChange={set("address")} data-testid="checkout-address" className="bg-navy-900/60 border-white/10 h-12" />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-2xl border border-flame/30 bg-navy-800 p-8 sticky top-24" data-testid="checkout-summary">
            <p className="section-label">ORDER SUMMARY</p>
            <div className="mt-6 space-y-3 text-sm">
              {[
                ["Project", SERVICES.find((s) => s.key === service)?.label],
                ["Delivery", service === "standard" ? "Standard" : "Rush Hour (live)"],
                ["Development hours", `${hours}h`],
                ["Base price", quote ? inr(quote.base_inr) : "—"],
                ["Selected currency", currency],
                ...(currency !== "INR" ? [["Converted amount", quote?.converted_total != null ? format(quote.converted_total) : "—"]] : []),
                ...(currency !== "INR" && quote?.fx_rate ? [["Exchange rate", `1 INR = ${quote.fx_rate} ${currency}`]] : []),
                ...(quote?.fx_updated_at ? [["Rate updated", new Date(quote.fx_updated_at).toLocaleString("en-IN")]] : []),
                [quote?.tax_label ? `${quote.tax_label}${quote.tax_rate ? ` (${quote.tax_rate}%)` : ""}` : "Tax", quote && quote.tax_amount_inr ? inr(quote.tax_amount_inr) : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-200 text-right font-medium">{v}</span>
                </div>
              ))}
              <div className="border-t border-white/10 pt-4 flex justify-between items-baseline">
                <span className="font-heading font-bold text-white">TOTAL</span>
                <span className="font-heading text-3xl font-black text-flame" data-testid="checkout-total">{totalDisplay}</span>
              </div>
            </div>

            <p className="mt-5 text-[10px] text-slate-500 leading-relaxed">
              Prices shown in {currency} are converted from our base INR pricing using the current exchange rate. Your
              payment provider/card issuer may apply additional currency conversion or international transaction charges.
              The exchange rate may change between quotation and payment; the final amount is calculated at payment creation.
            </p>

            <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="checkout-terms">
              <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-flame data-[state=checked]:border-flame" />
              <span className="text-xs text-slate-300 leading-relaxed">
                I agree to the <Link to="/terms-and-conditions" className="text-flame underline">Terms & Conditions</Link> and{" "}
                <Link to="/privacy-policy" className="text-flame underline">Privacy Policy</Link>.
              </span>
            </label>

            {config && !config.payments_enabled && (
              <p className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3.5 text-[11px] text-amber-300" data-testid="checkout-payments-pending">
                Online payments are being activated. Request an estimate and our team will share a payment link.
              </p>
            )}
            {quote && !quote.payable && currency !== "INR" && (
              <p className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3.5 text-[11px] text-amber-300" data-testid="checkout-currency-unavailable">
                This payment currency is currently unavailable. Please select another currency.
              </p>
            )}

            <Button
              onClick={pay}
              disabled={!terms || !customer.name || !customer.email || !payable || phase === "paying"}
              data-testid="checkout-pay-btn"
              className="mt-6 w-full bg-flame hover:bg-flame-hover disabled:opacity-40 text-white font-heading font-bold h-14 rounded-full"
            >
              <ShieldCheck className="w-5 h-5 mr-2" />
              {phase === "paying" ? "PROCESSING…" : `PAY ${totalDisplay}`}
            </Button>
            <p className="mt-4 flex items-center justify-center gap-2 text-[10px] text-slate-500">
              <Logo compact /> <span>Secure payments powered by Razorpay</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
